import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  toast,
} from "@devhop/ui";
import { CheckCircle2, Clock, Play } from "lucide-react";
import { formatTime24WithSeconds } from "@/shared/lib/date-time";
import type {
  GroupedKitchenTicket,
  KitchenTicketStatus,
  KitchenTicketWithDetails,
} from "../../domain/types";
import { useUpdateKitchenTicketStatus } from "../api/queries";
import { updateLocalKitchenTicketStatus } from "../lib/offline-sync/db";
import { KitchenTicketItemComponent } from "./KitchenTicketItem";

type KitchenTicketCardProps = {
  ticket?: KitchenTicketWithDetails;
  grouped?: GroupedKitchenTicket;
};

export function KitchenTicketCard({ ticket, grouped }: KitchenTicketCardProps) {
  const updateStatusMutation = useUpdateKitchenTicketStatus();

  // Use grouped tickets if provided, otherwise use single ticket
  const tickets = grouped ? grouped.tickets : ticket ? [ticket] : [];

  if (tickets.length === 0) {
    return null;
  }
  const firstTicket = tickets[0];
  if (!firstTicket) return null;

  const getStatusLabel = (status: KitchenTicketStatus) => {
    switch (status) {
      case "QUEUED":
        return "ລໍຖ້າ";
      case "IN_PROGRESS":
        return "ກຳລັງເຮັດ";
      case "DONE":
        return "ເຮັດແລ້ວ";
      default:
        return status;
    }
  };

  const getStatusColor = (status: KitchenTicketStatus) => {
    switch (status) {
      case "QUEUED":
        return "bg-blue-500 hover:bg-blue-500";
      case "IN_PROGRESS":
        return "bg-yellow-500 hover:bg-yellow-500";
      case "DONE":
        return "bg-green-500 hover:bg-green-500";
      default:
        return "";
    }
  };

  const handleStart = async (ticketId: string) => {
    try {
      if (ticketId.startsWith("local_kitchen_ticket")) {
        await updateLocalKitchenTicketStatus(ticketId, "IN_PROGRESS");
      } else {
        await updateStatusMutation.mutateAsync({
          ticketId,
          status: "IN_PROGRESS",
        });
      }
      toast.success("ເລີ່ມທຳການແລ້ວ");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  const handleComplete = async (ticketId: string) => {
    try {
      if (ticketId.startsWith("local_kitchen_ticket")) {
        await updateLocalKitchenTicketStatus(ticketId, "DONE");
      } else {
        await updateStatusMutation.mutateAsync({
          ticketId,
          status: "DONE",
        });
      }
      toast.success("ສຳເລັດແລ້ວ");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  const handleStartAll = async () => {
    const queuedTickets = tickets.filter((t) => t.ticket.status === "QUEUED");
    for (const t of queuedTickets) {
      await handleStart(t.ticket.id);
    }
  };

  const handleCompleteAll = async () => {
    const inProgressTickets = tickets.filter(
      (t) => t.ticket.status === "IN_PROGRESS",
    );
    for (const t of inProgressTickets) {
      await handleComplete(t.ticket.id);
    }
  };

  const getOrderTypeLabel = (orderType: string) => {
    switch (orderType) {
      case "DINE_IN":
        return "ກິນໃນຮ້ານ";
      case "TAKEAWAY":
        return "ສັ່ງກັບບ້ານ";
      case "DELIVERY":
        return "ຈັດສົ່ງ";
      default:
        return orderType;
    }
  };

  // Get unique order numbers
  const orderNumbers = Array.from(
    new Set(tickets.map((t) => t.order.orderNo)),
  ).sort((a, b) => a - b);

  // Get common table name (if all tickets have the same table)
  const tableNames = tickets
    .map((t) => t.order.tableName)
    .filter((name): name is string => name !== null);
  const commonTableName =
    tableNames.length > 0 && tableNames.every((name) => name === tableNames[0])
      ? tableNames[0]
      : null;

  // Get common customer name (if all tickets have the same customer)
  const customerNames = tickets
    .map((t) => t.order.customerName)
    .filter((name): name is string => name !== null);
  const commonCustomerName =
    customerNames.length > 0 &&
    customerNames.every((name) => name === customerNames[0])
      ? customerNames[0]
      : null;

  // Get overall status (if all tickets have the same status)
  const statuses = tickets.map((t) => t.ticket.status);
  const allSameStatus =
    statuses.length > 0 && statuses.every((s) => s === statuses[0]);
  const overallStatus = allSameStatus ? statuses[0] : null;

  // Get earliest queued time
  const queuedTimes = tickets.map((t) => new Date(t.ticket.queuedAt).getTime());
  const earliestQueuedTime = new Date(Math.min(...queuedTimes));

  // Check if all tickets are QUEUED or all are IN_PROGRESS
  const allQueued = tickets.every((t) => t.ticket.status === "QUEUED");
  const allInProgress = tickets.every((t) => t.ticket.status === "IN_PROGRESS");
  const hasQueued = tickets.some((t) => t.ticket.status === "QUEUED");
  const hasInProgress = tickets.some((t) => t.ticket.status === "IN_PROGRESS");
  const hasLocalTicket = tickets.some((t) =>
    t.ticket.id.startsWith("local_kitchen_ticket"),
  );

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-lg">
            {tickets.length > 1
              ? `#${orderNumbers.join(", #")}`
              : `#${firstTicket.order.orderNo}${
                  firstTicket.ticket.ticketSequence > 1
                    ? `-${firstTicket.ticket.ticketSequence}`
                    : ""
                }`}
          </h3>
          {overallStatus && (
            <Badge className={getStatusColor(overallStatus)}>
              {getStatusLabel(overallStatus)}
            </Badge>
          )}
          {!overallStatus && tickets.length > 1 && (
            <Badge variant="outline">ຫຼາຍສະຖານະ</Badge>
          )}
          {hasLocalTicket && <Badge variant="outline">Local</Badge>}
        </div>
        <div className="text-muted-foreground text-xs">
          {firstTicket.station.name}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 pt-0">
        <div className="space-y-1">
          {commonTableName && (
            <p className="text-muted-foreground text-sm">
              ໂຕະ: {commonTableName}
            </p>
          )}
          {commonCustomerName && (
            <p className="text-muted-foreground text-sm">
              ລູກຄ້າ: {commonCustomerName}
            </p>
          )}
          {tickets.length > 1 && (
            <p className="text-muted-foreground text-sm">
              ປະເພດ: {getOrderTypeLabel(firstTicket.order.orderType)}
            </p>
          )}
          {tickets.length === 1 && (
            <p className="text-muted-foreground text-sm">
              ປະເພດ: {getOrderTypeLabel(firstTicket.order.orderType)}
            </p>
          )}
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Clock className="h-3 w-3" />
            <span>{formatTime24WithSeconds(earliestQueuedTime)}</span>
            {tickets.length > 1 && (
              <span className="text-muted-foreground">
                ({tickets.length} ບິນ)
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 space-y-2 border-t pt-2">
          {tickets.map((t) => (
            <div key={t.ticket.id} className="space-y-1">
              {tickets.length > 1 && (
                <div className="mb-2 flex items-center gap-2 border-b pb-1">
                  <p className="font-medium text-sm">
                    Order #{t.order.orderNo}
                    {t.ticket.ticketSequence > 1 &&
                      `-${t.ticket.ticketSequence}`}
                  </p>
                  <Badge
                    variant="outline"
                    className={getStatusColor(t.ticket.status)}
                  >
                    {getStatusLabel(t.ticket.status)}
                  </Badge>
                </div>
              )}
              {t.items.map((item) => (
                <KitchenTicketItemComponent key={item.id} item={item} />
              ))}
            </div>
          ))}
        </div>

        {tickets.some((t) => t.ticket.note) && (
          <div className="rounded-md bg-muted p-2">
            {tickets
              .filter((t) => t.ticket.note)
              .map((t) => (
                <p key={t.ticket.id} className="text-muted-foreground text-xs">
                  <strong>ໝາຍເຫດ:</strong> {t.ticket.note}
                </p>
              ))}
          </div>
        )}

        <div className="flex gap-2 border-t pt-2">
          {tickets.length === 1 ? (
            <>
              {firstTicket.ticket.status === "QUEUED" && (
                <Button
                  className="flex-1"
                  onClick={() => handleStart(firstTicket.ticket.id)}
                  disabled={updateStatusMutation.isPending}
                >
                  <Play className="mr-2 h-4 w-4" />
                  ເລີ່ມ
                </Button>
              )}
              {firstTicket.ticket.status === "IN_PROGRESS" && (
                <Button
                  className="flex-1"
                  onClick={() => handleComplete(firstTicket.ticket.id)}
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  ສຳເລັດ
                </Button>
              )}
            </>
          ) : (
            <>
              {allQueued && (
                <Button
                  className="flex-1"
                  onClick={handleStartAll}
                  disabled={updateStatusMutation.isPending}
                >
                  <Play className="mr-2 h-4 w-4" />
                  ເລີ່ມທັງໝົດ ({tickets.length})
                </Button>
              )}
              {allInProgress && (
                <Button
                  className="flex-1"
                  onClick={handleCompleteAll}
                  disabled={updateStatusMutation.isPending}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  ສຳເລັດທັງໝົດ ({tickets.length})
                </Button>
              )}
              {(hasQueued || hasInProgress) && !allQueued && !allInProgress && (
                <div className="flex w-full flex-col gap-2">
                  {hasQueued && (
                    <Button
                      className="w-full"
                      onClick={handleStartAll}
                      disabled={updateStatusMutation.isPending}
                      variant="outline"
                    >
                      <Play className="mr-2 h-4 w-4" />
                      ເລີ່ມທີ່ລໍຖ້າ
                    </Button>
                  )}
                  {hasInProgress && (
                    <Button
                      className="w-full"
                      onClick={handleCompleteAll}
                      disabled={updateStatusMutation.isPending}
                      variant="outline"
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      ສຳເລັດທີ່ກຳລັງເຮັດ
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
